import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Button } from './Button';
import type { ReactNode, ElementType } from 'react';

// Icons (fallback for variants without illustrations)
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

// Illustrations
import {
  NoBookingsIllustration,
  NoVenuesIllustration,
  NoSearchResultsIllustration,
} from './illustrations';

export type EmptyStateVariant =
  | 'bookings'
  | 'venues'
  | 'search'
  | 'inventory'
  | 'users'
  | 'requests'
  | 'generic';

/**
 * Legacy variant-based interface (backward compatible)
 */
interface EmptyStateVariantProps {
  variant?: EmptyStateVariant;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: ReactNode;
}

/**
 * New simplified icon-based interface for mobile-responsive usage.
 * Pass an MUI icon component (e.g., EventBusy) as `iconComponent`.
 */
interface EmptyStateIconProps {
  iconComponent: ElementType;
  title: string;
  subtitle?: string;
  action?: ReactNode;
}

export type EmptyStateProps = EmptyStateVariantProps | EmptyStateIconProps;

function isIconProps(props: EmptyStateProps): props is EmptyStateIconProps {
  return 'iconComponent' in props;
}

const illustrationMap: Record<EmptyStateVariant, ReactNode> = {
  bookings: <NoBookingsIllustration />,
  venues: <NoVenuesIllustration />,
  search: <NoSearchResultsIllustration />,
  inventory: <InventoryOutlinedIcon />, // Fallback to icon for now
  users: <PeopleOutlineIcon />, // Fallback to icon for now
  requests: <ReceiptLongOutlinedIcon />, // Fallback to icon for now
  generic: <SearchOffOutlinedIcon />, // Fallback to icon for now
};

const defaultContent: Record<EmptyStateVariant, { title: string; description: string }> = {
  bookings: {
    title: 'No bookings yet',
    description: 'Book a venue to see your bookings here.',
  },
  venues: {
    title: 'No venues found',
    description: 'Try adjusting your filters or check back later.',
  },
  search: {
    title: 'No results found',
    description: 'Try different search terms or filters.',
  },
  inventory: {
    title: 'No inventory items',
    description: 'Add items to your inventory catalog.',
  },
  users: {
    title: 'No users found',
    description: 'Users will appear here once they register.',
  },
  requests: {
    title: 'No pending requests',
    description: 'All requests have been processed.',
  },
  generic: {
    title: 'Nothing here yet',
    description: 'Content will appear here when available.',
  },
};

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px 24px',
  textAlign: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
}));

const IllustrationContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 24,
  '& svg': {
    maxWidth: '100%',
    height: 'auto',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 600,
  fontSize: '1.25rem',
  color: theme.palette.text.primary,
  marginBottom: 8,
}));

const Description = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  maxWidth: 320,
  marginBottom: 24,
}));

export function EmptyState(props: EmptyStateProps) {
  // New icon-based interface
  if (isIconProps(props)) {
    const { iconComponent: Icon, title, subtitle, action } = props;
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          p: { xs: 2, md: 4 },
        }}
      >
        <Icon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography
          variant="body1"
          sx={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 600,
            fontSize: '1rem',
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              mt: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
        {action && <Box sx={{ mt: 2 }}>{action}</Box>}
      </Box>
    );
  }

  // Legacy variant-based interface
  const { variant = 'generic', title, description, actionLabel, onAction, icon } = props;
  const defaultTitle = title || defaultContent[variant].title;
  const defaultDescription = description || defaultContent[variant].description;
  const displayIllustration = icon || illustrationMap[variant];

  return (
    <Container>
      <IllustrationContainer>{displayIllustration}</IllustrationContainer>
      <Title>{defaultTitle}</Title>
      <Description>{defaultDescription}</Description>
      {actionLabel && onAction && (
        <Button variant="primary" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </Container>
  );
}
